import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["processingStart", "startTime"];
import CartographerReporter, { THIRTY_SECONDS } from './CartographerReporter';
import { onVisibilityHidden } from '../visibility';
import { resetFirstInputPolyfill, firstInputPolyfill } from '../BFCacheRestore';
import performanceNow from '../../vendor/performanceNow';
import PerformanceEventBus from '../PerformanceEventBus';
import PageStateTracker from '../PageStateTracker';
const onBFCacheRestore = callback => {
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      callback(event);
    }
  }, true);
};
export default class VitalsReporter extends CartographerReporter {
  /**
   * Get the singleton instance of VitalsReporter.
   * Uses window-scoped storage so the instance is shared across all JS bundles
   * on the same page, preventing duplicate LCP reports.
   */
  static getInstance(options) {
    const w = window;
    if (!w[VitalsReporter.WINDOW_KEY]) {
      w[VitalsReporter.WINDOW_KEY] = new VitalsReporter(options);
    }
    return w[VitalsReporter.WINDOW_KEY];
  }
  static resetInstance() {
    window[VitalsReporter.WINDOW_KEY] = undefined;
  }
  getPageState() {
    const pageStateTracker = PageStateTracker.getInstance();
    const pageStateSummary = pageStateTracker.getStateSummary();
    const currentSnapshot = pageStateTracker.getSnapshot();
    return {
      currentOnline: currentSnapshot.online,
      currentHidden: currentSnapshot.hidden,
      wasEverHidden: pageStateSummary.wasEverHidden,
      wasEverOffline: pageStateSummary.wasEverOffline
    };
  }
  constructor(options) {
    super(options);
    this.resolved = {};
    this.stopped = false;
    this.firstHiddenTime = document.visibilityState === 'hidden' ? 0 : Infinity;
    this.interactionMap = new Map();
    this.latestLCP = null;
    window.addEventListener('visibilitychange', event => {
      this.firstHiddenTime = Math.min(this.firstHiddenTime, event.timeStamp);
    }, {
      once: true
    });
    const debounce = () => {
      clearTimeout(this.flushQueueTimeout);
      this.flushQueueTimeout = setTimeout(() => {
        this.flushPerformanceQueue();
        // clear the map once the performance action buffer is cleared, to avoid memory leak
        this.interactionMap.clear();
      }, THIRTY_SECONDS);
    };
    const processFIDEntry = perfEntry => {
      // some browsers no longer support FID and send an error instead of a PerformanceEventTiming object
      if (perfEntry && typeof perfEntry === 'object' && 'toJSON' in perfEntry && typeof perfEntry.toJSON === 'function') {
        const _toJSON = perfEntry.toJSON(),
          {
            processingStart: processingStart,
            startTime: startTime
          } = _toJSON,
          perfData = _objectWithoutPropertiesLoose(_toJSON, _excluded);
        // ignore entries if the page was not in the foreground when the first input occured
        if (startTime < this.firstHiddenTime) {
          const inputDelay = processingStart - startTime;
          this.pushPerformanceAction(this.lastRouteInfo, 'first-input', Object.assign({}, perfData, {
            processingStart,
            startTime,
            inputDelay,
            pageState: this.getPageState()
          }));
          debounce();
        }
      }
    };
    const processUserInteractionEntry = perfEntry => {
      const {
        interactionId,
        target,
        name
      } = perfEntry;
      if (interactionId > 0) {
        let interaction = this.interactionMap.get(interactionId);
        if (!interaction) {
          interaction = {
            latency: 0,
            entries: [],
            interactionNames: ''
          };
          this.interactionMap.set(interactionId, interaction);
        }
        interaction.entries.push(perfEntry);
        interaction.latency = Math.max(perfEntry.duration, interaction.latency);
        const testId = target && target !== null && target !== void 0 && target.hasAttribute('data-test-id') ? target === null || target === void 0 ? void 0 : target.getAttribute('data-test-id') : '';
        let tagName = '';
        if (target) {
          const element = target;
          const baseTagName = element.tagName.toLowerCase();
          const elementId = element.id;
          const classNames = element.getAttribute('class') || '';
          tagName = baseTagName;
          if (elementId) {
            tagName += `#${elementId}`;
          }
          if (classNames && classNames.trim()) {
            const classes = classNames.trim().split(/\s+/).join('.');
            tagName += `.${classes}`;
          }
        }
        if (name && name.length > 0) {
          interaction.interactionNames = interaction.interactionNames.concat(interaction.interactionNames.length > 0 ? ' ' : '', name);
        }
        this.evictPerformanceAction(interactionId);
        this.pushPerformanceAction(this.lastRouteInfo, 'event', {
          interactionId,
          interactionNames: interaction.interactionNames,
          latency: interaction.latency,
          elapsedMs: performanceNow(),
          testId,
          tagName,
          pageState: this.getPageState()
        });
        debounce();
      }
    };
    const entryHandler = entry => {
      processFIDEntry(entry);
    };

    /*
      When the page is restored from the back/forward cache, re-add event listeners for FID
      and manually push FID data to the performance actions queue since the performance observer
      won't be able to catch it.
    */
    onBFCacheRestore(() => {
      resetFirstInputPolyfill();
      firstInputPolyfill(entryHandler);
    });

    // Observe performance entries
    PerformanceEventBus.getInstance().subscribe('long-animation-frame', perfEntry => {
      // For each script in the long animation frame, emit a longtask event
      // Only log scripts with duration >= 50ms
      perfEntry.scripts.forEach(script => {
        if (script.duration >= 50) {
          const scriptData = script.toJSON();
          this.pushPerformanceAction(this.lastRouteInfo, 'longtask', Object.assign({
            name: script.windowAttribution
          }, scriptData, {
            entryType: 'longtask',
            pageState: this.getPageState()
          }));
        }
      });
      debounce();
    });
    PerformanceEventBus.getInstance().subscribe('first-input', perfEntry => {
      processFIDEntry(perfEntry);
    });
    PerformanceEventBus.getInstance().subscribe('event', perfEntry => {
      processUserInteractionEntry(perfEntry);
    });
    PerformanceEventBus.getInstance().subscribe('paint', perfEntry => {
      if (perfEntry.name === 'first-contentful-paint') {
        const paintData = {
          name: perfEntry.name,
          startTime: perfEntry.startTime,
          entryType: perfEntry.entryType,
          elapsedMs: performanceNow(),
          pageState: this.getPageState()
        };
        this.pushPerformanceAction(this.lastRouteInfo, 'paint', paintData);
        debounce();
      }
    });
    PerformanceEventBus.getInstance().subscribe('largest-contentful-paint', perfEntry => {
      const element = perfEntry.element;
      const {
        entryType,
        size,
        startTime,
        renderTime,
        loadTime,
        id,
        url
      } = perfEntry.toJSON();
      const tagName = (element === null || element === void 0 ? void 0 : element.tagName) || (element === null || element === void 0 ? void 0 : element.nodeName) || 'unknown';
      const className = (element === null || element === void 0 ? void 0 : element.className) || '';
      const paintData = {
        entryType,
        size,
        startTime,
        renderTime,
        element: className ? `${tagName}.${className}` : tagName,
        loadTime,
        elapsedMs: performanceNow(),
        id,
        url,
        pageState: this.getPageState()
      };
      // Store the latest LCP instead of pushing immediately
      // It will be pushed when the page becomes hidden or user interacts
      this.latestLCP = {
        route: this.lastRouteInfo,
        data: paintData
      };
    });

    // Handle first user interaction to finalize LCP
    const finalizeOnInteraction = () => {
      if (this.latestLCP) {
        this.pushPerformanceAction(this.latestLCP.route, 'largest-contentful-paint', this.latestLCP.data);
        this.latestLCP = null;
        debounce();
      }
    };
    PerformanceEventBus.getInstance().subscribe('layout-shift', perfEntry => {
      // Filter out user-initiated shifts and insignificant shifts
      if (perfEntry.hadRecentInput || perfEntry.value < 0.001) {
        return;
      }
      const {
        entryType,
        value,
        startTime,
        sources,
        hadRecentInput,
        lastInputTime
      } = perfEntry.toJSON();
      const layoutShiftData = {
        entryType,
        value,
        startTime,
        sources,
        hadRecentInput,
        lastInputTime,
        elapsedMs: performanceNow(),
        pageState: this.getPageState()
      };
      this.pushPerformanceAction(this.lastRouteInfo, 'layout-shift', layoutShiftData);
      debounce();
    });
    // Store cleanup functions for interaction listeners
    const interactionCleanupFunctions = [];

    // Listen for first interaction events that finalize LCP
    const eventTypes = ['keydown', 'mousedown', 'pointerdown', 'touchstart'];
    eventTypes.forEach(eventType => {
      const handler = () => {
        finalizeOnInteraction();
        // Clean up remaining listeners after first interaction
        interactionCleanupFunctions.forEach(cleanup => cleanup());
      };
      window.addEventListener(eventType, handler, {
        capture: true,
        once: true
      });
      interactionCleanupFunctions.push(() => window.removeEventListener(eventType, handler, {
        capture: true
      }));
    });

    // Handle visibility change to flush remaining entries
    onVisibilityHidden(() => {
      // Push the final LCP before flushing
      if (this.latestLCP) {
        this.pushPerformanceAction(this.latestLCP.route, 'largest-contentful-paint', this.latestLCP.data);
        this.latestLCP = null;
      }
      // Clean up interaction listeners since LCP is now finalized
      interactionCleanupFunctions.forEach(cleanup => cleanup());
      // Flush any remaining performance actions
      this.flushPerformanceQueue();
    });
  }
  evictPerformanceAction(interactionId) {
    this.performanceActions = this.performanceActions.filter(action => action.data.interactionId !== interactionId);
  }
  __setFirstHiddenTime(time) {
    this.firstHiddenTime = time;
  }
  report(action) {
    if (action.type === 'COMPONENT_RENDERED' || this.resolved[action.payload.entry.id] || this.stopped) {
      return;
    }
    switch (action.type) {
      case 'ROUTE_TIMEOUT_EXPIRED':
        {
          const {
            routeSpec: {
              route
            }
          } = action.payload;
          this.lastRouteInfo = {
            route
          };
          break;
        }
      case 'ROUTE_FAILED':
        {
          const {
            entry: {
              pathname
            },
            routeSpec
          } = action.payload;
          const {
            route
          } = routeSpec;
          this.lastRouteInfo = {
            pathname,
            route
          };
          break;
        }
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_SUCCEEDED':
        {
          const {
            entry: {
              pathname
            },
            extra: {
              scenario
            },
            routeSpec
          } = action.payload;
          const {
            route
          } = routeSpec;
          this.lastRouteInfo = {
            pathname,
            route,
            scenario
          };
          break;
        }
      default:
    }
    switch (action.type) {
      case 'ROUTE_UNEXPECTED':
        {
          this.stopped = true;
          break;
        }
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_SUCCEEDED':
      case 'ROUTE_TIMEOUT_EXPIRED':
      case 'ROUTE_FAILED':
        {
          this.resolved[action.payload.entry.id] = true;
          break;
        }
      default:
    }
  }
}
VitalsReporter.WINDOW_KEY = '__rhumb_vitals_reporter__';